import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { subscribe, subscribeWithPassword } from '../../services/ws-service';
import { leaveLobby, lobbyTopic } from '../../services/lobby-service';
import axios from '../../services/api-axios';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import useLobbyMessageHandler from '../../message-handler/useLobbyMessageHandler';
import { connectToLobby } from '../../store/lobbySlice';
import styles from './CreateLobbyForm.module.css';

type GameMode = 'SINGLE' | '101' | '131' | '151';

const CreateLobbyForm = () => {
    const [expanded, setExpanded] = useState(false);
    const [lobbyName, setLobbyName] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [password, setPassword] = useState('');
    const [gameMode, setGameMode] = useState<GameMode>('SINGLE');

    const onLobbyMessage = useLobbyMessageHandler();
    const lobbyId = useAppSelector((state) => state.lobby.lobbyId);
    const username = useAppSelector((state) => state.onlineUsers.username);
    const dispatch = useAppDispatch();

    const handleLobbyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLobbyName(e.target.value);
    };

    const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsPrivate(e.target.value === 'private');
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
    };

    const handleGameModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGameMode(e.target.value as GameMode);
    };

    const createLobby = async (e: React.FormEvent) => {
        e.preventDefault();

        const sessionTarget = gameMode === 'SINGLE' ? undefined : Number(gameMode);

        // Leave first, and only then create: a session that creates a lobby while
        // still holding another strands both of them on the server for good. The
        // subscription that follows is not optional either — it is the only thing
        // that lets the server release this lobby when we are done with it.
        await leaveLobby(lobbyId, username);

        try {
            const res = await axios.post('/rest/lobby', {
                lobbyName: lobbyName,
                lobbyPassword: password,
                sessionTarget
            });
            if (password) {
                subscribeWithPassword(lobbyTopic(res.data), onLobbyMessage, password);
            } else {
                subscribe(lobbyTopic(res.data), onLobbyMessage);
            }
            dispatch(connectToLobby(res.data));
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.panel}>
                <button
                    type="button"
                    className={styles.header}
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                >
                    <span className={styles.headerIcon}>➕</span>
                    <div className={styles.headerText}>
                        <h2 className={styles.title}>Create New Lobby</h2>
                        <p className={styles.subtitle}>Don't see one you like? Start your own.</p>
                    </div>
                    <span className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}>▾</span>
                </button>

                <AnimatePresence initial={false}>
                    {expanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: 'easeInOut' }}
                            className={styles.collapseBody}
                        >
                            <form onSubmit={createLobby} className={styles.form}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Lobby Name</label>
                                    <input
                                        type="text"
                                        value={lobbyName}
                                        onChange={handleLobbyNameChange}
                                        placeholder="Enter lobby name..."
                                        className={styles.input}
                                        autoFocus
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.privacyLabel}>Privacy Settings</label>
                                    <div className={styles.privacyOptions}>
                                        <label className={`${styles.privacyOption} ${!isPrivate ? styles.privacyOptionActive : ''}`}>
                                            <input
                                                type="radio"
                                                name="privacy"
                                                value="public"
                                                checked={!isPrivate}
                                                onChange={handlePrivacyChange}
                                                className={styles.radioInput}
                                            />
                                            <div className={`${styles.radioDot} ${!isPrivate ? styles.radioDotActive : ''}`}>
                                                {!isPrivate && <div className={styles.radioDotFill} />}
                                            </div>
                                            <div>
                                                <div className={styles.privacyOptionTitle}>📢 Public</div>
                                                <div className={styles.privacyOptionSubtitle}>Anyone can join</div>
                                            </div>
                                        </label>

                                        <label className={`${styles.privacyOption} ${isPrivate ? styles.privacyOptionActive : ''}`}>
                                            <input
                                                type="radio"
                                                name="privacy"
                                                value="private"
                                                checked={isPrivate}
                                                onChange={handlePrivacyChange}
                                                className={styles.radioInput}
                                            />
                                            <div className={`${styles.radioDot} ${isPrivate ? styles.radioDotActive : ''}`}>
                                                {isPrivate && <div className={styles.radioDotFill} />}
                                            </div>
                                            <div>
                                                <div className={styles.privacyOptionTitle}>🔒 Private</div>
                                                <div className={styles.privacyOptionSubtitle}>Password required</div>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                {isPrivate && (
                                    <div className={styles.passwordField}>
                                        <label className={styles.label}>Password</label>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={handlePasswordChange}
                                            placeholder="Enter password..."
                                            className={styles.input}
                                        />
                                    </div>
                                )}

                                <div className={styles.formGroup}>
                                    <label className={styles.privacyLabel}>Game Mode</label>
                                    <div className={styles.gameModeOptions}>
                                        {([
                                            { value: 'SINGLE', title: '🎲 Single Game', subtitle: 'One hand decides it' },
                                            { value: '101', title: '🏆 Session to 101', subtitle: 'First to 101 points' },
                                            { value: '131', title: '🏆 Session to 131', subtitle: 'First to 131 points' },
                                            { value: '151', title: '🏆 Session to 151', subtitle: 'First to 151 points' }
                                        ] as { value: GameMode; title: string; subtitle: string }[]).map((option) => (
                                            <label
                                                key={option.value}
                                                className={`${styles.privacyOption} ${styles.gameModeOption} ${gameMode === option.value ? styles.privacyOptionActive : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="gameMode"
                                                    value={option.value}
                                                    checked={gameMode === option.value}
                                                    onChange={handleGameModeChange}
                                                    className={styles.radioInput}
                                                />
                                                <div className={`${styles.radioDot} ${gameMode === option.value ? styles.radioDotActive : ''}`}>
                                                    {gameMode === option.value && <div className={styles.radioDotFill} />}
                                                </div>
                                                <div>
                                                    <div className={styles.privacyOptionTitle}>{option.title}</div>
                                                    <div className={styles.privacyOptionSubtitle}>{option.subtitle}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <button type="submit" className={styles.submitButton}>
                                    🚀 Create Lobby
                                </button>
                            </form>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default CreateLobbyForm;
